/**
 * Staff Profile Service
 *
 * Manages school-specific staff profile data (roles, responsibilities, extracurriculars).
 * Personal/professional fields (title, PERSAL, SACE) live on the user document
 * and are managed via UserService.updateUserProfile().
 *
 * Collection: schools/{schoolId}/staffProfiles/{userId}
 * Doc ID = user's auth UID for easy lookup.
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';

class StaffProfileService {
  constructor() {
    this.collectionName = 'staffProfiles';
  }

  /**
   * Get a single staff profile by userId
   */
  async getProfile(schoolId, userId) {
    if (!schoolId || !userId) return null;

    const docRef = doc(db, 'schools', schoolId, this.collectionName, userId);
    const snapshot = await getDoc(docRef);

    if (!snapshot.exists()) return null;
    return { id: snapshot.id, ...snapshot.data() };
  }

  /**
   * Get all staff profiles for a school
   */
  async getAll(schoolId) {
    if (!schoolId) return [];
    const colRef = collection(db, 'schools', schoolId, this.collectionName);
    const snapshot = await getDocs(colRef);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  }

  /**
   * Create or update a staff profile (merge mode)
   */
  async saveProfile(schoolId, userId, data) {
    if (!schoolId || !userId) {
      throw new Error('schoolId and userId are required');
    }

    const docRef = doc(db, 'schools', schoolId, this.collectionName, userId);
    await setDoc(docRef, {
      ...data,
      userId,
      updatedAt: serverTimestamp()
    }, { merge: true });

    return { id: userId, ...data };
  }
}

export const staffProfileService = new StaffProfileService();
export default staffProfileService;
